import { IMGS_PATH } from 'constants/image';
import clsx from 'clsx';
import Icon from 'components/ui/Icon';
import { useEffect, useRef, useState } from 'react';
import styles from './ImageUploadSpot.module.scss';
import type Uppy from '@uppy/core';
import type { TypeChecking } from '@uppy/core';
import type { ChangeEvent } from 'react';
import type { Image } from 'types/Utilities';

type Props = {
  index: number | string;
  spot: {
    title?: string;
    description?: string;
    placeholder?: Image;
  };
  uppy: Uppy.Uppy<TypeChecking>;
  hideTitles?: boolean;
};

const ImageUploadSpot = ({
  index,
  spot,
  uppy,
  hideTitles,
}: Props): JSX.Element => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [hasImage, setHasImage] = useState(false);
  const [imageId, setImageId] = useState<string | null>(null);
  const [backgroundImage, setBackgroundImage] = useState(
    spot.placeholder?.url || '',
  );

  const addFileToUpload = (event: ChangeEvent): void => {
    const element = event.target as HTMLInputElement;
    const file = element.files ? element.files[0] : null;

    if (file) {
      try {
        const response = uppy.addFile({
          source: 'file input',
          name: file.name,
          type: file.type,
          data: file,
        });

        if (!file.type.match(/image/)) {
          setBackgroundImage(`${IMGS_PATH.PUBLIC}/upload-placeholder.jpg`);
        }

        setImageId(response);
      } catch (e) {
        // eslint-disable-next-line
        // @ts-ignore
        if (e.isRestriction) {
          // handle restrictions
          console.log('Restriction error:', e);
        } else {
          // handle other errors
          console.error(e);
        }
      }

      setHasImage(true);
    } else {
      setBackgroundImage(spot.placeholder?.url || '');
      setHasImage(false);
    }
  };

  const removeFileFromUpload = (id: string | null): void => {
    if (id) {
      uppy.removeFile(id as string);
      setBackgroundImage(spot.placeholder?.url || '');
      setHasImage(false);
    }
  };

  useEffect(() => {
    uppy.on('thumbnail:generated', (file, preview) => {
      if (file.id === imageId) {
        setBackgroundImage(preview);
      }
    });
  }, [imageId]);

  return (
    <div className={styles.uploadSpot}>
      {!hideTitles && <p className={styles.title}>{spot.title}</p>}
      <div className={styles.previewContainer}>
        <div
          className={clsx(styles.preview, hasImage && styles.hasImage)}
          style={{
            backgroundImage: `url(${backgroundImage})`,
          }}
          onClick={() =>
            hasImage
              ? removeFileFromUpload(imageId)
              : fileInputRef?.current?.click()
          }
        >
          {hasImage ? (
            <button className={styles.crossButton}>
              <Icon className={styles.crossIcon} type="Cross" color="#ffffff" />
            </button>
          ) : (
            <button className={styles.plusButton}>
              <Icon className={styles.plusIcon} type="Plus" color="#155263" />
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            hidden
            id={`upl_${index}`}
            accept="image/*"
            capture="user"
            onChange={(event) => {
              addFileToUpload(event);
            }}
          />
        </div>
      </div>
      <p>{spot.description}</p>
    </div>
  );
};

export default ImageUploadSpot;
